import * as Dialog from '@radix-ui/react-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { getExportDownloadUrl, listExports, triggerExport } from '../services/reportService';
import { IPortalExport } from '../types';
import { DateTimeDisplay } from './common/DateTimeDisplay';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

interface ExportTrackerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exportType: 'daily_summary' | 'transactions';
  currentFilters: Record<string, unknown>;
}

const STATUS_CLASSES: Record<IPortalExport['status'], string> = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  processing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const hasPendingOrProcessing = (exports: IPortalExport[]): boolean =>
  exports.some((e) => e.status === 'pending' || e.status === 'processing');

export const ExportTrackerModal = ({ open, onOpenChange, exportType, currentFilters }: ExportTrackerModalProps) => {
  const queryClient = useQueryClient();
  const [isTriggering, setIsTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { data: exports = [], isLoading } = useQuery({
    queryKey: ['portal-exports'],
    queryFn: listExports,
    enabled: open,
    refetchInterval: (query) => {
      const data = query.state.data as IPortalExport[] | undefined;
      return data && hasPendingOrProcessing(data) ? 5000 : false;
    },
  });

  const handleGenerateExport = async () => {
    setIsTriggering(true);
    setTriggerError('');
    try {
      await triggerExport(exportType, currentFilters);
      await queryClient.invalidateQueries({ queryKey: ['portal-exports'] });
    } catch {
      setTriggerError('Failed to trigger export. Please try again.');
    } finally {
      setIsTriggering(false);
    }
  };

  const handleDownload = async (exportItem: IPortalExport) => {
    setDownloadingId(exportItem._id);
    try {
      const result = await getExportDownloadUrl(exportItem._id);
      if (result?.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
      } else {
        const apiBase = import.meta.env.VITE_PORTAL_API_URL as string;
        const downloadUrl = `${apiBase}/reports/exports/${exportItem._id}/download`;
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      const apiBase = import.meta.env.VITE_PORTAL_API_URL as string;
      const downloadUrl = `${apiBase}/reports/exports/${exportItem._id}/download`;
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]',
            'w-full max-w-lg max-h-[80vh] flex flex-col',
            'rounded-xl border border-white/10 bg-[#1a1a2e] shadow-2xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
            <Dialog.Title className="text-base font-semibold text-white">Export History</Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-white">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Generate button area */}
          <div className="px-6 py-4 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-3">
              <Button
                onClick={handleGenerateExport}
                disabled={isTriggering}
                className="text-white font-medium gap-2"
                style={{ background: '#8C16C7', border: 'none' }}
              >
                {isTriggering ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Generate Export
              </Button>
              <span className="text-xs text-muted-foreground">Exports the current filtered data as CSV</span>
            </div>
            {triggerError && (
              <div
                className="mt-3 text-sm p-3 rounded-lg"
                style={{
                  background: 'rgba(239,68,68,0.08)',
                  color: '#fca5a5',
                  border: '1px solid rgba(239,68,68,0.20)',
                }}
              >
                {triggerError}
              </div>
            )}
          </div>

          {/* Export list */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-16 rounded-lg bg-white/5 animate-pulse" />
                ))}
              </div>
            ) : exports.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Download className="h-10 w-10 text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">No exports yet. Click Generate to create one.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {exports.map((exportItem) => (
                  <div key={exportItem._id} className="p-4 rounded-lg border border-white/10 bg-white/5 space-y-2">
                    {/* Top row: type, status, download */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-white capitalize">
                            {exportItem.type.replace('_', ' ')}
                          </span>
                          <Badge className={cn('text-xs border capitalize', STATUS_CLASSES[exportItem.status])}>
                            {exportItem.status === 'processing' && <RefreshCw className="h-3 w-3 mr-1 animate-spin" />}
                            {exportItem.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          <DateTimeDisplay date={exportItem.createdAt} className="text-xs" />
                        </p>
                      </div>
                      {exportItem.status === 'completed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={downloadingId === exportItem._id}
                          onClick={() => handleDownload(exportItem)}
                          className="flex-shrink-0 gap-1.5 h-8 text-xs"
                        >
                          {downloadingId === exportItem._id ? (
                            <RefreshCw className="h-3 w-3 animate-spin" />
                          ) : (
                            <Download className="h-3 w-3" />
                          )}
                          Download
                        </Button>
                      )}
                    </div>

                    {/* Filters summary */}
                    {exportItem.filters && Object.keys(exportItem.filters).length > 0 && (
                      <div className="text-xs text-muted-foreground space-y-0.5 pt-1 border-t border-white/5">
                        {exportItem.filters.startDate && exportItem.filters.endDate && (
                          <p>
                            <span className="text-white/50">Range:</span>{' '}
                            {new Date(exportItem.filters.startDate).toLocaleDateString()} -{' '}
                            {new Date(exportItem.filters.endDate).toLocaleDateString()}
                          </p>
                        )}
                        {exportItem.filters.currency && (
                          <p>
                            <span className="text-white/50">Currency:</span> {exportItem.filters.currency}
                          </p>
                        )}
                        {exportItem.filters.status && (
                          <p>
                            <span className="text-white/50">Status:</span> {exportItem.filters.status}
                          </p>
                        )}
                        {exportItem.filters.transactionType && (
                          <p>
                            <span className="text-white/50">Type:</span> {exportItem.filters.transactionType}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Error message */}
                    {exportItem.status === 'failed' && exportItem.errorMessage && (
                      <div
                        className="text-xs p-2 rounded"
                        style={{
                          background: 'rgba(239,68,68,0.08)',
                          color: '#fca5a5',
                          border: '1px solid rgba(239,68,68,0.15)',
                        }}
                      >
                        <span className="font-medium">Error:</span> {exportItem.errorMessage}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer note */}
          <div className="px-6 py-3 border-t border-white/10 flex-shrink-0">
            <p className="text-xs text-muted-foreground">
              Showing last 20 exports. Completed exports expire after a set period.
            </p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
